import { useEffect, useState, useCallback, useRef } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RefreshCw, Download, Link2 } from "lucide-react";
import { toast } from "sonner";

function LoyverseTab() {
  const { org } = useAuth();
  const [status, setStatus] = useState(null);
  const [stores, setStores] = useState([]);
  const [fromDate, setFromDate] = useState("2025-01-01");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/loyverse/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/loyverse/stores").then((r) => setStores(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(typeof msg === "function" ? msg(r.data) : msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la operación");
    } finally { setBusy(false); }
  };

  const mapStore = async (storeId, branchId, accountKey) => {
    await run(() => api.patch(`/loyverse/stores/${storeId}/mapping`, { branch_id: branchId || null, account: accountKey }),
      "Tienda vinculada a sucursal");
  };

  return (
    <div className="space-y-6">
      <Card data-testid="loyverse-status-card">
        <CardHeader><CardTitle className="text-base font-semibold font-heading flex items-center gap-2">
          <Link2 className="w-4 h-4" /> Conexión Loyverse
        </CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <div>Estado: {status?.configured
              ? <Badge className="bg-green-100 text-green-800 border-green-200" variant="outline">Token configurado</Badge>
              : <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">Sin token</Badge>}</div>
            <div>Tickets importados: <span className="font-num font-semibold">{status?.receipts ?? 0}</span></div>
            <div>Tiendas: <span className="font-num font-semibold">{status?.stores ?? 0}</span></div>
          </div>
          {(status?.accounts || []).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {status.accounts.map((a) => (
                <div key={a.key} data-testid={`loyverse-account-${a.key}`} className="border rounded-lg p-3 text-sm bg-slate-50">
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {a.stores} tiendas · <span className="font-num">{a.receipts}</span> tickets · última sync: {a.last_sync_at ? fmtDate(a.last_sync_at) : "nunca"}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="loyverse-sync-stores" variant="outline" disabled={busy}
              onClick={() => run(() => api.post("/loyverse/sync/stores"), (d) => `${d.stores} tiendas y ${d.payment_types} métodos de pago sincronizados`)}>
              <RefreshCw className="w-4 h-4 mr-2" /> Sincronizar tiendas
            </Button>
            <Button data-testid="loyverse-sync-incremental" variant="outline" disabled={busy}
              onClick={() => run(() => api.post("/loyverse/sync/incremental"), (d) => `${d.saved} tickets actualizados`)}>
              <RefreshCw className="w-4 h-4 mr-2" /> Sync incremental
            </Button>
          </div>
          <div className="flex items-end gap-2 border-t pt-4">
            <div className="space-y-1.5">
              <Label>Importación histórica desde</Label>
              <Input data-testid="loyverse-import-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-44" />
            </div>
            <Button data-testid="loyverse-import-initial" disabled={busy}
              className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2"
              onClick={() => run(() => api.post("/loyverse/sync/initial", { from_date: `${fromDate}T00:00:00Z` }), (d) => `${d.saved} tickets importados`)}>
              <Download className="w-4 h-4" /> Importar histórico
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="loyverse-stores-card">
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Mapeo tienda Loyverse → sucursal</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Cuenta</TableHead><TableHead>Tienda Loyverse</TableHead><TableHead>ID</TableHead><TableHead>Sucursal asignada</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {stores.map((s) => (
                <TableRow key={`${s.loyverse_account}-${s.loyverse_store_id}`} data-testid={`store-row-${s.loyverse_store_id}`}>
                  <TableCell><Badge variant="outline" className="text-xs">{s.loyverse_account_name || s.loyverse_account}</Badge></TableCell>
                  <TableCell className="font-medium text-sm">{s.name}</TableCell>
                  <TableCell className="font-num text-xs text-slate-500">{s.loyverse_store_id.slice(0, 8)}…</TableCell>
                  <TableCell>
                    <Select value={s.branch_id || "none"} onValueChange={(v) => mapStore(s.loyverse_store_id, v === "none" ? null : v, s.loyverse_account)}>
                      <SelectTrigger data-testid={`store-map-${s.loyverse_store_id}`} className="w-56 h-9"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {org.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {stores.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400 text-sm">
                  Pulsa "Sincronizar tiendas" para descargar las tiendas de Loyverse.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function UsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "READ_ONLY" });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    if (user?.role === "ADMIN") api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "ADMIN") return <div className="text-sm text-slate-500 py-8">Solo administradores pueden gestionar usuarios.</div>;

  const create = async () => {
    try {
      await api.post("/auth/users", form);
      toast.success("Usuario creado");
      setForm({ email: "", name: "", password: "", role: "READ_ONLY" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear usuario");
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/auth/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? "Usuario desactivado" : "Usuario activado");
    load();
  };

  const removeUser = async () => {
    try {
      await api.delete(`/auth/users/${toDelete.id}`);
      toast.success("Usuario eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Crear usuario</CardTitle></CardHeader>
        <CardContent className="flex gap-3 flex-wrap items-end">
          <div className="space-y-1.5"><Label>Nombre</Label>
            <Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-48" /></div>
          <div className="space-y-1.5"><Label>Email</Label>
            <Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-56" /></div>
          <div className="space-y-1.5"><Label>Contraseña</Label>
            <Input data-testid="new-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-44" /></div>
          <div className="space-y-1.5"><Label>Rol</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="new-user-role" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["ADMIN", "FINANCE", "MANAGER", "READ_ONLY"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <Button data-testid="create-user-button" onClick={create} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white">Crear</Button>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                  <TableCell className="text-sm">{u.active ? "Activo" : "Inactivo"}</TableCell>
                  <TableCell className="text-right">
                    {u.email !== user?.email && (
                      <div className="flex justify-end gap-1.5">
                        <Button data-testid={`user-toggle-${u.id}`} size="sm" variant="outline" onClick={() => toggleActive(u)}>
                          {u.active ? "Desactivar" : "Activar"}
                        </Button>
                        <Button data-testid={`user-delete-${u.id}`} size="sm" variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setToDelete(u)}>
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará definitivamente a <strong>{toDelete?.email}</strong>. Esta acción queda registrada en auditoría y no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" onClick={removeUser}
              className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BranchesTab() {
  const { org } = useAuth();
  const [name, setName] = useState("");
  const [branches, setBranches] = useState(org.branches);

  useEffect(() => setBranches(org.branches), [org]);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/branches", { name: name.trim() });
    toast.success("Sucursal creada");
    setName("");
    const r = await api.get("/org");
    setBranches(r.data.branches);
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base font-semibold font-heading">Sucursales</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input data-testid="new-branch-name" placeholder="Nueva sucursal..." value={name} onChange={(e) => setName(e.target.value)} className="w-64" />
          <Button data-testid="add-branch-button" onClick={add} variant="outline">Añadir</Button>
        </div>
        <div className="space-y-2">
          {branches.map((b) => (
            <div key={b.id} data-testid={`branch-item-${b.id}`} className="flex items-center justify-between border rounded-lg px-4 py-2.5">
              <span className="text-sm font-medium">{b.name}</span>
              <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">{b.active ? "Activa" : "Inactiva"}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DriveTab() {
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const intervalsRef = useRef([]);
  useEffect(() => () => intervalsRef.current.forEach(clearInterval), []);

  const startPoll = (stopWhen) => {
    let n = 0;
    const t = setInterval(async () => {
      n++;
      const s = await api.get("/drive/status").then((r2) => r2.data).catch(() => null);
      if (s) setStatus(s);
      if (n > 60 || (s && stopWhen(s))) clearInterval(t);
    }, 10000);
    intervalsRef.current.push(t);
  };

  const load = useCallback(() => {
    api.get("/drive/status").then((r) => setStatus(r.data)).catch(() => {
      setStatus({ configured: false, connected: false, folders: [], loadError: true });
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("drive") === "connected") setMsg("Google Drive conectado correctamente");
    const err = p.get("drive_error");
    if (err) {
      const friendly = {
        access_denied: "Google denegó el acceso. Causa habitual: la pantalla de consentimiento está en modo Pruebas y tu Gmail no está añadido como usuario de prueba en Google Cloud (Pantalla de consentimiento OAuth → Audiencia → Usuarios de prueba).",
        exchange_failed: "No se pudo completar el intercambio con Google. Inténtalo de nuevo.",
        no_refresh_token: "Google no devolvió token de larga duración. Reintenta la conexión.",
      };
      setMsg(friendly[err] || `Error de conexión: ${err}`);
    }
    if (p.get("drive") || err) window.history.replaceState({}, "", "/configuracion?tab=drive");
  }, []);

  const [connecting, setConnecting] = useState(false);

  const connect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const r = await api.get("/drive/auth-url");
      // Pestaña nueva sin 'noopener' en el feature string: con noopener window.open devuelve null
      // y el fallback navegaría también la pestaña actual (rompiendo el fix anti-iframe 403)
      const w = window.open(r.data.url, "_blank");
      if (!w || w.closed || typeof w.closed === "undefined") {
        window.location.href = r.data.url;
        setConnecting(false);
      } else {
        try { w.opener = null; } catch {}
        toast.info("Se ha abierto Google en una pestaña nueva. Tras autorizar, volverás aquí automáticamente.");
        startPoll((s) => {
          if (s.connected) toast.success("Google Drive conectado");
          return s.connected;
        });
        setTimeout(() => setConnecting(false), 6000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando URL de autorización");
      setConnecting(false);
    }
  };

  const searchFolders = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/drive/folders?q=${encodeURIComponent(search)}`);
      setResults(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error buscando carpetas");
    } finally { setBusy(false); }
  };

  const selectFolder = async (f, kind) => {
    try {
      await api.post("/drive/folders/select", { folder_id: f.id, folder_name: f.name, kind });
      toast.success(`Carpeta "${f.name}" asignada a ${kind}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error asignando la carpeta");
    }
  };

  const scan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/drive/scan");
      toast.success(r.data.already_running
        ? "Ya hay un escaneo en curso"
        : "Escaneo iniciado — las facturas irán apareciendo progresivamente");
      startPoll((s) => !s.scanning);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en el escaneo");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Google Drive? Las facturas ya importadas se conservan.")) return;
    await api.delete("/drive/connection");
    toast.success("Google Drive desconectado");
    load();
  };

  const folderFor = (kind) => (status?.folders || []).find((f) => f.kind === kind);

  return (
    <div className="space-y-6">
      <Card data-testid="drive-status-card">
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Conexión Google Drive</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {msg && <div className="text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2" data-testid="drive-msg">{msg}</div>}
          {status?.connection_error && (
            <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2" data-testid="drive-error-msg">
              <strong>La conexión con Google Drive caducó.</strong> Google revoca el acceso cada 7 días mientras la app OAuth esté en modo "Pruebas".
              Pulsa "Conectar Google Drive" para reautorizar — las facturas ya importadas se conservan.
            </div>
          )}
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <div>Estado: {status?.connected
              ? <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Conectado{status.email ? ` · ${status.email}` : ""}</Badge>
              : <Badge variant="outline" className="text-slate-600">No conectado</Badge>}</div>
            <div>Facturas importadas desde Drive: <span className="font-num font-semibold" data-testid="drive-files-processed">{status ? status.files_processed : "—"}</span></div>
            {status?.scanning && (
              <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50 gap-1.5" data-testid="drive-scanning-badge">
                <RefreshCw className="w-3 h-3 animate-spin" /> Escaneando...
              </Badge>
            )}
            <div>Último escaneo: <span className="text-slate-500">{status?.last_scan_at ? fmtDate(status.last_scan_at) : "nunca"}</span></div>
          </div>
          {!status?.connected && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 max-w-xl">
                Autoriza el acceso de solo lectura con tu cuenta de Google. La app nunca modifica tus archivos originales.
                Si Google muestra el aviso "app no verificada", pulsa <strong>Configuración avanzada → Ir a itacate-finance</strong>.
              </p>
              <p className="text-xs text-slate-400 max-w-xl">
                En Google Cloud → Clientes → tu cliente OAuth → "URI de redireccionamiento autorizados" debe aparecer
                <code className="mx-1 px-1 bg-slate-100 rounded break-all">{`${window.location.origin}/api/drive/callback`}</code>
                (si conectas desde otro dominio, añade también el suyo).
              </p>
              <Button data-testid="drive-connect-button" onClick={connect} disabled={connecting} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white">
                {connecting ? "Abriendo Google..." : "Conectar Google Drive"}
              </Button>
            </div>
          )}
          {status?.connected && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {["digitales", "fisicas"].map((kind) => {
                  const f = folderFor(kind);
                  return (
                    <div key={kind} data-testid={`drive-folder-${kind}`} className="border rounded-lg p-3 text-sm">
                      <div className="text-xs uppercase tracking-wider text-slate-500">Facturas {kind}</div>
                      <div className="font-medium mt-1">{f ? f.folder_name : "Sin carpeta asignada"}</div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t pt-4 space-y-3">
                <div className="flex gap-2">
                  <Input data-testid="drive-folder-search" placeholder="Buscar carpeta (ej: Digitales)..." value={search}
                    onChange={(e) => setSearch(e.target.value)} className="w-72"
                    onKeyDown={(e) => e.key === "Enter" && searchFolders()} />
                  <Button data-testid="drive-search-button" variant="outline" onClick={searchFolders} disabled={busy}>Buscar</Button>
                </div>
                {results.length > 0 && (
                  <div className="space-y-2" data-testid="drive-folder-results">
                    {results.map((f) => (
                      <div key={f.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                        <span className="text-sm font-medium">{f.name}</span>
                        <div className="flex gap-2">
                          <Button data-testid={`drive-select-digitales-${f.id}`} size="sm" variant="outline" onClick={() => selectFolder(f, "digitales")}>Usar como Digitales</Button>
                          <Button data-testid={`drive-select-fisicas-${f.id}`} size="sm" variant="outline" onClick={() => selectFolder(f, "fisicas")}>Usar como Físicas</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="drive-scan-button" onClick={scan} disabled={busy || status?.scanning || !(status?.folders || []).length}
                  variant="outline" className="gap-2">
                  <RefreshCw className={`w-4 h-4 ${busy || status?.scanning ? "animate-spin" : ""}`} /> Escanear carpetas ahora
                </Button>
                <Button data-testid="drive-disconnect-button" onClick={disconnect} variant="outline"
                  className="text-red-700 border-red-200 hover:bg-red-50">
                  Desconectar Drive
                </Button>
              </div>
              <p className="text-xs text-slate-400">Escaneo automático cada hora (máx. 30 facturas nuevas por ciclo; el resto entra en los siguientes). Solo PDF, JPG y PNG. Los originales nunca se modifican.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PlantillasTab() {
  return (
    <Card data-testid="plantillas-card" className="max-w-xl">
      <CardHeader><CardTitle className="text-base font-semibold font-heading">Plantillas de importación</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="border rounded-xl p-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Movimientos de tarjeta</div>
            <p className="text-xs text-slate-500 mt-1">
              Excel con las columnas Fecha operación / Nombre del comercio / Importe.
              Los cargos se escriben en positivo y las devoluciones en negativo.
            </p>
          </div>
          <Button asChild size="sm" data-testid="download-card-template"
            className="bg-[#D9381E] hover:bg-[#B91C1C] text-white shrink-0">
            <a href="/plantillas/plantilla_movimientos_tarjeta.xlsx" download>Descargar</a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


export default function Configuracion() {
  const initialTab = new URLSearchParams(window.location.search).get("tab") || "loyverse";
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="space-y-6" data-testid="configuracion-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Configuración</h1>
        <p className="text-sm text-slate-500 mt-1">Empresa, sucursales, usuarios e integraciones</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="config-tabs">
          <TabsTrigger value="loyverse" data-testid="tab-loyverse">Loyverse</TabsTrigger>
          <TabsTrigger value="drive" data-testid="tab-drive">Google Drive</TabsTrigger>
          <TabsTrigger value="branches" data-testid="tab-branches">Sucursales</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
          <TabsTrigger value="plantillas" data-testid="tab-plantillas">Plantillas</TabsTrigger>
        </TabsList>
        <TabsContent value="loyverse" className="mt-6"><LoyverseTab /></TabsContent>
        <TabsContent value="drive" className="mt-6"><DriveTab /></TabsContent>
        <TabsContent value="branches" className="mt-6"><BranchesTab /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="plantillas" className="mt-6"><PlantillasTab /></TabsContent>
      </Tabs>
    </div>
  );
}
